import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { EntityTypes } from '../../../../new-lib/constants/common';
import { Image, EntityDetailsTypes } from '../../../../new-lib/types/entity';
import { getImagePath } from '../../../../helpers/common';
import { getImageSources } from '../../../../helpers/images';
import FloorPlansIcon from '../../../../public/assets/icons/entity/FloorPlansIcon';
import GalleryIcon from '../../../../public/assets/icons/entity/GalleryIcon';
import MasterPlanIcon from '../../../../public/assets/icons/entity/MasterPlanIcon';
import QuickView from '../../../../new-components/display-system/QuickView';
import Map from '../../../../new-components/map/Map';
import EntityDetail from './EntityDetail';
import { logEvent } from '../../../../new-lib/analytics';
import ViewOnMapIcon from '../../../../new-components/svg-icons/ViewOnMapIcon';
import router from 'next/router';
import { useDispatch } from 'react-redux';
import { setSelectedCompound } from '../../../../redux/slices/mapSlice';
import { DefaultMapEntity } from '../../../map/types';

interface ChildProps {
  entityType: EntityTypes;
  galleryImages: Image[] | string[];
  floorPlanImages?: Image[];
  masterPlanImage: string;
  mapData: DefaultMapEntity;
}

const EntityDetails = ({
  entityType,
  galleryImages,
  floorPlanImages,
  masterPlanImage,
  mapData
}: ChildProps): JSX.Element => {
  const { t } = useTranslation(['entity']);
  const [showDetail, setShowDetail] = useState(false);
  const [detailType, setDetailType] = useState<EntityDetailsTypes>();
  const [GalleryComponent, setGalleryComponent] = useState<any>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (showDetail && !GalleryComponent) {
      const Gallery = dynamic(
        () => import('../../../../new-components/Gallery')
      );
      setGalleryComponent(Gallery);
    }

    return () => {
      setGalleryComponent(null);
      document.documentElement?.classList?.remove('fslightbox-open');
    };
  }, [showDetail]);

  const handleShowDetail = (detailType?: EntityDetailsTypes) => {
    setShowDetail(!showDetail);
    setDetailType(detailType);
    logEvent.click(
      `${detailType}_click`,
      `${entityType}DetailsSection`,
      'other'
    );
  };

  const handleMapAction = () => {
    if (mapData?.geometry?.coordinates) {
      dispatch(setSelectedCompound(mapData));
      router.push(`/search?view=map`);
    } else {
      handleShowDetail(EntityDetailsTypes.MAP);
    }
  };

  const getImages = (detailType?: EntityDetailsTypes) => {
    switch (detailType) {
      case EntityDetailsTypes.FLOOR_PLAN:
        return getImageSources(floorPlanImages);
      case EntityDetailsTypes.GALLERY:
        return getImageSources(galleryImages);
      case EntityDetailsTypes.MASTER_PLAN:
        return [getImagePath(masterPlanImage)];
      default:
        return undefined;
    }
  };

  return (
    <DetailsContainer>
      <h3 className="headline-3 entity-title">{t('details')}</h3>
      <div className="entities-container">
        {entityType == EntityTypes.PROPERTY &&
          floorPlanImages &&
          floorPlanImages?.length > 0 && (
            <EntityDetail
              clickHandler={() => {
                handleShowDetail(EntityDetailsTypes.FLOOR_PLAN);
              }}
              icon={<FloorPlansIcon />}
              label={t('floor_plan')}
            />
          )}
        {galleryImages.length > 0 && (
          <EntityDetail
            clickHandler={() => {
              handleShowDetail(EntityDetailsTypes.GALLERY);
            }}
            icon={<GalleryIcon />}
            label={t('gallery')}
          />
        )}
        {masterPlanImage && (
          <EntityDetail
            clickHandler={() => {
              handleShowDetail(EntityDetailsTypes.MASTER_PLAN);
            }}
            icon={<MasterPlanIcon />}
            label={t('master_plan')}
          />
        )}
        {mapData?.long > 0 && mapData?.lat > 0 && (
          <EntityDetail
            clickHandler={() => handleMapAction()}
            icon={<ViewOnMapIcon />}
            label={t('view_on_map')}
            displayNew={mapData?.geometry?.coordinates !== undefined}
          />
        )}
      </div>
      <QuickView isOpen={showDetail} setIsOpen={setShowDetail} isMap={true}>
        {detailType == EntityDetailsTypes.MAP && (
          <Map
            mapData={{ long: mapData?.long, lat: mapData?.lat }}
            name={mapData?.name}
          />
        )}
        {detailType != EntityDetailsTypes.MAP && GalleryComponent && (
          <GalleryComponent
            show={showDetail}
            sources={getImages(detailType)}
            key={detailType}
          />
        )}
      </QuickView>
    </DetailsContainer>
  );
};

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  .entities-container {
    display: flex;
    flex-direction: row;
    gap: 16px;
  }
`;
export default EntityDetails;
